import { cn } from "@/lib/utils";

import { cva, VariantProps } from "class-variance-authority";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export const inputVariants = cva(
  "flex w-full cursor-text overflow-hidden rounded-md border border-input bg-background focus-within:border-tertiary-400 focus-within:ring-1 focus-within:ring-tertiary-400",
  {
    variants: {
      containerSize: {
        default: "h-10",
        sm: "h-9",
        xs: "h-8",
      },
    },
    defaultVariants: {
      containerSize: "default",
    },
  },
);

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  leadingComponent?: React.ReactNode;
  suffixText?: React.ReactNode;
  onValidateInternalValue?: (e: React.ChangeEvent<HTMLInputElement>) => boolean;
  onValidate?: (e: React.ChangeEvent<HTMLInputElement>) => boolean;
  classNameInput?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      classNameInput,
      type,
      containerSize,
      leadingComponent,
      suffixText,
      onValidateInternalValue,
      onValidate,
      onChange,
      onClick,
      onFocus,
      onBlur,
      onKeyDown,
      value,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const inputValue = onValidateInternalValue ? internalValue : value;

    return (
      <div
        className={cn(
          inputVariants({
            containerSize,
          }),
          className,
        )}
      >
        {!!leadingComponent && (
          <div
            className="ml-3 flex h-full cursor-text items-center text-sm text-text-secondary"
            onMouseDown={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
          >
            {leadingComponent}
          </div>
        )}
        <input
          ref={inputRef}
          type={type}
          data-slot="input"
          className={cn(
            "mx-3 h-full w-full flex-1 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none md:text-sm",
            classNameInput,
          )}
          value={`${inputValue}${isFocused || inputValue === "" ? "" : (suffixText ?? "")}`}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          onKeyDown={onKeyDown}
          onChange={(e) => {
            if (onValidateInternalValue) {
              if (onValidateInternalValue(e)) {
                setInternalValue(e.target.value);
              } else {
                return;
              }
            }

            if (onValidate && !onValidate(e)) {
              return;
            }

            onChange?.(e);
          }}
          {...props}
        />
      </div>
    );
  },
);

export { Input };
